import { deserialize } from '@monolith/legacy/services/utils/jsonapi-parser';

import ProductDraft, {
  ProductIntegration,
} from '@monolith/legacy/types/account/account-products/catalog-integration/product-integration';
import { getSingleCatalogIntegration } from '@monolith/legacy/services/api/brand-catalog-integration';
import { Amount } from '@core/types/amount';

interface Drafts {
  [index: string]: ProductIntegration;
}

export interface State {
  drafts: Drafts;
  loading: boolean;
}

const createInitialState = (): State => ({
  drafts: {},
  loading: false,
});

type AmountOrNull = Amount | null | undefined;

const convertAmountToCents = (amountObject: AmountOrNull): AmountOrNull => {
  return typeof amountObject === 'object'
    ? {
        ...amountObject,
        amount: amountObject.amount * 100,
      }
    : null;
};

export default {
  namespaced: true,
  state: createInitialState,
  actions: {
    async fetchProductIntegration({ commit }, uuid: string): Promise<void> {
      commit('TOGGLE_LOADING', true);
      const response = await getSingleCatalogIntegration(uuid);
      const { data } = deserialize(response);
      commit('SET_PRODUCT_INTEGRATION', data);
      commit('FORMAT_PRODUCT_DRAFT_PRICES', uuid);
      commit('FORMAT_PRODUCT_DRAFT_VARIANT_PRICES', uuid);
      commit('FORMAT_PRODUCT_DRAFT_DESCRIPTION', uuid);
      commit('TOGGLE_LOADING', false);
    },
  },
  mutations: {
    TOGGLE_LOADING(state: State, value: boolean): void {
      state.loading = value;
    },

    SET_PRODUCT_INTEGRATION(state: State, draft: ProductIntegration): void {
      state.drafts = {
        ...state.drafts,
        [draft.uuid]: draft,
      };
    },

    FORMAT_PRODUCT_DRAFT_PRICES(state: State, uuid: string): void {
      state.drafts[uuid].internal_state.discount_rate = state.drafts[uuid].internal_state.discount_rate ?? 0;

      state.drafts[uuid].internal_state.vat_rate = state.drafts[uuid].internal_state.vat_rate ?? 0;

      state.drafts[uuid].internal_state.original_wholesale_price = convertAmountToCents(
        state.drafts[uuid].internal_state.original_wholesale_price ?? {
          amount: 0,
          currency: state.drafts[uuid].internal_state.currency,
        }
      );

      state.drafts[uuid].internal_state.wholesale_price = convertAmountToCents(
        state.drafts[uuid].internal_state.wholesale_price ?? {
          amount: 0,
          currency: state.drafts[uuid].internal_state.currency,
        }
      );

      state.drafts[uuid].internal_state.retail_price = convertAmountToCents(
        state.drafts[uuid].internal_state.retail_price ?? {
          amount: 0,
          currency: state.drafts[uuid].internal_state.currency,
        }
      );
    },

    FORMAT_PRODUCT_DRAFT_VARIANT_PRICES(state: State, uuid: string): void {
      state.drafts[uuid].internal_state.variants = state.drafts[uuid].internal_state.variants.map((variant) => {
        variant.price.original_wholesale_price = convertAmountToCents(
          variant.price.original_wholesale_price ?? {
            amount: 0,
            currency: variant.price.currency,
          }
        );

        variant.price.retail_price = convertAmountToCents(
          variant.price.retail_price ?? {
            amount: 0,
            currency: variant.price.currency,
          }
        );

        return variant;
      });
    },

    FORMAT_PRODUCT_DRAFT_DESCRIPTION(state: State, uuid: string): void {
      state.drafts[uuid].internal_state.description = state.drafts[uuid].internal_state.description || '';
    },
  },
  getters: {
    getProductDraft:
      ({ drafts }: { drafts: Drafts }) =>
      (uuid: string): ProductDraft => {
        return drafts[uuid]
          ? Object.assign({}, drafts[uuid].internal_state, {
              ...drafts[uuid].internal_state,
              uuid,
            })
          : undefined;
      },
  },
};
